import React, { useMemo, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { FaSearch } from 'react-icons/fa';
import ReactPaginate from 'react-paginate';

import NodataBox from '../../components/CustomUI/NoDataBox';

const TestList = ({ isOpen, onClose, testList }) => {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedTest, setSelectedTest] = useState(null);
  const [confirmPopupOpen, setConfirmPopupOpen] = useState(false);

  const itemsPerPage = 5;

  // Filter and pagination logic
  const filteredTests = useMemo(() => {
    return testList.filter((test) =>
      Object.values(test).some((value) =>
        value.toString().toLowerCase().includes(search.toLowerCase()),
      ),
    );
  }, [testList, search]);

  const offset = currentPage * itemsPerPage;
  const currentItems = useMemo(
    () => filteredTests.slice(offset, offset + itemsPerPage),
    [filteredTests, offset, itemsPerPage],
  );
  const pageCount = Math.ceil(filteredTests.length / itemsPerPage);

  const handlePageClick = (data) => setCurrentPage(data.selected);

  const handleBookTest = (test) => {
    setSelectedTest(test);
    setConfirmPopupOpen(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-60">
      <div className="relative w-full max-w-4xl rounded-lg bg-white p-6 shadow-lg">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <AiOutlineClose size={20} />
        </button>
        {testList.length > 0 ? (
          <React.Fragment>
            {/* Search Bar */}
            <div className="relative mb-4 flex items-center">
              <input
                type="text"
                placeholder="Search tests..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-1/2 rounded border border-gray-300 py-2 pl-10 pr-4 text-sm"
              />
              <FaSearch className="absolute left-3 text-gray-500" />
            </div>

            {/* Tests Table */}
            <div className="max-h-96 overflow-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-blue-900 text-left">
                    <th className="border-b p-2 text-white">Disease</th>
                    <th className="border-b p-2 text-white">Broad Category</th>
                    <th className="border-b p-2 text-white">Method</th>
                    <th className="border-b p-2 text-white">Lab</th>
                    <th className="border-b p-2 text-right text-white">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((test) => (
                    <tr key={test.test_accession} className="hover:bg-gray-50">
                      <td className="max-w-xs overflow-hidden overflow-ellipsis whitespace-nowrap p-2">
                        {test.condition_identifiers}
                      </td>
                      <td className="max-w-xs overflow-hidden overflow-ellipsis whitespace-nowrap p-2">
                        {test.method_categories}
                      </td>
                      <td className="max-w-xs overflow-hidden overflow-ellipsis whitespace-nowrap p-2">
                        {test.methods}
                      </td>
                      <td className="max-w-xs overflow-hidden overflow-ellipsis whitespace-nowrap p-2">
                        {test.name_of_laboratory}
                      </td>
                      <td className="p-2 text-right">
                        <button
                          onClick={() => handleBookTest(test)}
                          className="rounded bg-yellow-500 px-3 py-1 text-xs text-white hover:bg-yellow-600"
                        >
                          Book
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pageCount > 1 && (
              <div className="mt-4 flex justify-start">
                <ReactPaginate
                  pageCount={pageCount}
                  onPageChange={handlePageClick}
                  containerClassName="flex space-x-2"
                  pageClassName="px-3 py-1 bg-gray-200 border rounded"
                  activeClassName="bg-blue-500 text-white"
                  previousLabel="Previous"
                  nextLabel="Next"
                  breakLabel="..."
                />
              </div>
            )}
          </React.Fragment>
        ) : (
          <NodataBox
            mainText="You test details found"
            subText="We will update the test details soon"
          />
        )}
      </div>

      {/* Confirmation Popup */}
      {confirmPopupOpen && (
        <ConfirmBookingPopup
          test={selectedTest}
          onClose={() => setConfirmPopupOpen(false)}
        />
      )}
    </div>
  );
};

// Confirmation Popup Component
const ConfirmBookingPopup = ({ test, onClose }) => {
  const handleConfirmBooking = () => {
    alert(`Test Booking Confirmed for: ${test.test_accession}`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-60">
      <div className="relative w-80 rounded-lg bg-white p-6 shadow-lg">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <AiOutlineClose size={20} />
        </button>
        <h2 className="mb-4 text-lg font-semibold">Confirm Booking</h2>
        <p className="mb-6 text-sm">
          Are you sure you want to book the test with Accession{' '}
          <strong>{test.test_accession}</strong>?
        </p>
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="rounded bg-gray-200 px-4 py-2 text-sm hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmBooking}
            className="rounded bg-blue-500 px-4 py-2 text-sm text-white hover:bg-blue-600"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestList;
